<template>
  <LayoutOverlay @closeOverlay="closeModal">
    <main class="ticket-container">
      <CreateTicketHeader
        :title="t('general__data__new-ticket')"
        @closeCreateTicketModal="closeModal"
      />
      <DividerHorizontal style="margin-bottom: 16px" />
      <CreateTicketContent class="ticket-container__content" />
      <CreateTicketFooter
        class="ticket-container__footer"
        @cancelCreateTicket="closeModal"
        @createTicket="handleCreateTicket"
      />
    </main>
  </LayoutOverlay>
</template>
<script lang="ts" setup>
import { computed, provide, reactive } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useSelectionStore } from '@/pinia/selection'
import { CreateTicket, createTicket } from '@/services/tickets.service'

import CreateTicketContent from '@/components/features/tickets/CreateTicketContent.vue'
import CreateTicketFooter from '@/components/features/tickets/CreateTicketFooter.vue'
import CreateTicketHeader from '@/components/features/tickets/CreateTicketHeader.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import * as Sentry from '@sentry/vue'

interface EMITS {
  (e: 'closeCreateTicketModal', val: boolean)
}
const emits = defineEmits<EMITS>()

const { t } = useI18n()
const store = useStore()
const selectionStore = useSelectionStore()
const { clickedEntityType } = storeToRefs(selectionStore)
const selectedObjectId = computed(() => {
  return store.getters.selectedObjectId
})

const formData = reactive<CreateTicket>({
  title: '',
  description: null,
  categoryId: '',
  typeId: '',
  // @ts-expect-error // see type EntityType with allowed values – clickedEntityType can be other values if create would be enabled for other entities
  entityConnection: [{ id: selectedObjectId.value, entityType: clickedEntityType.value }], // Start with single, but could be multible in the future
  priority: 'LOWEST',
  status: 'NOT_IN_PROGRESS',
  startDate: null,
  dueDate: null
})
provide<CreateTicket>('formDataCreateErrand', formData)

function closeModal() {
  emits('closeCreateTicketModal', false)
}
async function handleCreateTicket() {
  if (!formData) return
  try {
    const ticket = await createTicket(formData)
    if (ticket) {
      store.commit('setInfoMessage', {
        type: 'success',
        message: `${t('general__data__ticket-created')}`
      })
      closeModal()
    }
  } catch (error) {
    Sentry.captureException(error)
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__data__ticket-create-failed')}`
    })
  }
}
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.ticket-container {
  background-color: variables.$color-pure-white;
  border-radius: variables.$border-radius-small;
  display: flex;
  flex-direction: column;
  height: fit-content;
  margin: 32px auto auto;
  max-height: 85vh;
  min-height: 472px; // Todo: consider if this is necessary (nice when loading spinner is shown, but not otherwise)
  overflow: hidden;
  padding: 24px;
  width: 800px;
  @media screen and (max-width: 1024px) {
    border-radius: 0;
    height: calc(100vh - variables.$layout-top-bar-height);
    left: 0;
    margin: auto;
    max-height: unset;
    top: 0; // variables.$layout-top-bar-height;
    transform: unset;
    width: 100vw;
  }
  &__content {
    margin-bottom: auto;
    margin-left: -4px; // For dropdown focus state to not be cut off
    padding-left: 4px; // For dropdown focus state to not be cut off
    width: 100%;
  }
  &__footer {
    margin-top: 16px;
  }
  &__loading-spinner {
    margin: auto;
  }
  &__section {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;
  }
}
</style>
